'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import {
  AUTH_GATE_HIDE_SOCIAL_PARAM,
  AUTH_GATE_ORIGIN_PARAM,
  AUTH_GATE_PARAM,
} from '@/shared/constants/search-params';
import { EMAIL_VERIFIED_COOKIE } from '@/features/auth/shared/helpers/constants/verification-cookies';
import { readClientCookie } from '@/shared/utils/client-cookies';
import { openAuthGate, type AuthGateView } from '@/features/auth/shared/state/auth-gate.store';

const VIEW_BY_PARAM: Record<string, AuthGateView> = {
  login: 'login',
  register: 'register',
};

const stripAuthGateParams = (params: URLSearchParams) => {
  [AUTH_GATE_PARAM, AUTH_GATE_HIDE_SOCIAL_PARAM, AUTH_GATE_ORIGIN_PARAM].forEach((param) =>
    params.delete(param),
  );
  const search = params.toString();
  const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', nextUrl);
};

export const AuthGateUrlOpener = () => {
  const { loading, authenticatedUser } = useUser();
  const pathname = usePathname();

  useEffect(() => {
    if (loading) return;

    const params = new URLSearchParams(window.location.search);
    const authParam = params.get(AUTH_GATE_PARAM);
    if (!authParam) return;

    const view = VIEW_BY_PARAM[authParam];
    if (!view) return;

    if (authenticatedUser) {
      stripAuthGateParams(params);
      return;
    }

    const verifiedEmail = readClientCookie(EMAIL_VERIFIED_COOKIE);
    const hideSocial = params.get(AUTH_GATE_HIDE_SOCIAL_PARAM) === '1';
    const origin = params.get(AUTH_GATE_ORIGIN_PARAM) === 'client' ? 'client' : 'web';

    stripAuthGateParams(params);

    openAuthGate({
      view,
      prefillEmail: verifiedEmail || '',
      hideSocial,
      origin,
      trigger: 'verification_return',
    });
  }, [authenticatedUser, loading, pathname]);

  return null;
};
